import { designVariables } from "@studio3t/design-variables";
import {
  BadgeMode,
  BadgeType,
  ButtonMediumSecondaryLeadingIcon,
  GenericBadge,
  GenericInput,
  Icon,
  IconSVG,
  InputMode,
  InputType,
  StyledTableScroll,
  styledTableClassNames,
  Tooltip,
} from "@studio3t/generic-components";
import {
  type Column,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import { auditApi } from "@/api/client";
import { ListPageHeader } from "@/components/ListPageHeader";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import { SortableHeader } from "@/components/SortableHeader";
import { TablePagination } from "@/components/TablePagination";
import { usePersistedState } from "@/hooks/usePersistedState";
import type { AuditEntry } from "@/types/api";
import { userStorage } from "@/utils/userStorage";
import {
  AGENT_ACTOR_FILTER,
  AgentBadge,
  ALL_STATUS_CLASSES,
  AUDIT_ACTION_GROUPS,
  type AuditActionGroup,
  AuditLogFilterSidebar,
  badgeIconColor,
  STATUS_CLASS_ROWS,
  type Timeframe,
} from "./AuditLogFilterSidebar";
import { parseTimeframe, serializeTimeframe } from "./auditTimeframeStorage";

// The /audit-log page (Figma TableCard 13759-112324): the /logs card
// anatomy — filter sidebar as the left rail, content region beside it —
// but with the Audit sizing (270px rail, the design-system TableSimple
// with 186px columns) and a fetched, paginated table instead of a live
// stream. The region stacks the "Filter logs" input row, the table, and
// the footer bar carrying Export + pagination; the table hugs its rows,
// so short pages leave card background below the footer.

const SINGLE_LINE = designVariables.type.singleLine;
const TABLE = designVariables.ui.table;
/* Sum of the design column widths (5×186 + 120) plus the design's own
   ~186px for the message column (Figma TableSimple 13759-118888 at
   1244) — the readable minimum the region holds before scrolling. */
const TABLE_MIN_WIDTH = 1236;

// Per-user persisted UI state (the /logs page's userStorage recipe):
// defaults are stored as absence, so keys only exist while the user
// deviates from them.
const STORAGE_KEYS = {
  collapsed: "audit:filters-collapsed",
  actions: "audit:actions",
  statuses: "audit:statuses",
  filterText: "audit:filter-text",
  resource: "audit:resource",
  actor: "audit:actor",
  pageSize: "audit:page-size",
  timeframe: "audit:timeframe",
} as const;

const PAGE_SIZES = [10, 25, 50, 100];
// One fetch feeds the client-side filters/pagination; the timeframe is
// the only filter narrowing the query itself.
const FETCH_LIMIT = 1000;
// Background refresh cadence. Audit entries are historical, not
// operational state — a relaxed poll (vs the Pipelines list's 5s) keeps
// the table current during long sessions without hammering the endpoint.
const AUDIT_POLL_MS = 30_000;

function readStoredSet<T>(
  key: string,
  allValues: ReadonlyArray<T>,
): ReadonlySet<T> {
  try {
    const raw = userStorage.getItem(key);
    if (!raw) return new Set(allValues);
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(allValues);
    return new Set(parsed.filter((v): v is T => allValues.includes(v as T)));
  } catch {
    return new Set(allValues);
  }
}

// A selection Set persisted like the /logs level checkboxes: everything
// selected is the default (stored as absence), and stale stored values
// are dropped against the known list.
function usePersistedSelection<T>(key: string, allValues: ReadonlyArray<T>) {
  const [selected, setSelected] = useState<ReadonlySet<T>>(() =>
    readStoredSet(key, allValues),
  );

  useEffect(() => {
    if (selected.size === allValues.length) userStorage.removeItem(key);
    else userStorage.setItem(key, JSON.stringify([...selected]));
  }, [selected, key, allValues]);

  const toggle = useCallback((value: T) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const setAll = useCallback(
    (on: boolean) => {
      setSelected(new Set(on ? allValues : []));
    },
    [allValues],
  );

  return { selected, toggle, setAll };
}

function usePersistedCollapsed() {
  const [collapsed, setCollapsed] = useState(
    () => userStorage.getItem(STORAGE_KEYS.collapsed) === "true",
  );

  useEffect(() => {
    if (collapsed) userStorage.setItem(STORAGE_KEYS.collapsed, "true");
    else userStorage.removeItem(STORAGE_KEYS.collapsed);
  }, [collapsed]);

  return [collapsed, setCollapsed] as const;
}

function usePersistedFilterText() {
  const [filterText, setFilterText] = useState(
    () => userStorage.getItem(STORAGE_KEYS.filterText) ?? "",
  );

  useEffect(() => {
    if (filterText) userStorage.setItem(STORAGE_KEYS.filterText, filterText);
    else userStorage.removeItem(STORAGE_KEYS.filterText);
  }, [filterText]);

  return [filterText, setFilterText] as const;
}

// A single-value dropdown filter (resource, actor) remembered per user;
// "all" is the unfiltered sentinel and clears the stored key.
function usePersistedChoice(key: string) {
  const [value, setValue] = useState(() => userStorage.getItem(key) ?? "all");

  const handleChange = useCallback(
    (v: string) => {
      setValue(v);
      if (v === "all") userStorage.removeItem(key);
      else userStorage.setItem(key, v);
    },
    [key],
  );

  return [value, handleChange] as const;
}

function readStoredPageSize(): number {
  const raw = Number(userStorage.getItem(STORAGE_KEYS.pageSize));
  return PAGE_SIZES.includes(raw) ? raw : PAGE_SIZES[0];
}

// The timeframe narrows the query server-side; everything else filters
// the fetched window client-side, so toggling checkboxes never refetches.
// After the initial load the window polls in the background: silent
// refreshes on a chained timeout (the next one only schedules once the
// previous request settles, so slow responses never overlap), keeping
// the last good data when a poll fails.
function useAuditEntries(timeframe: Timeframe) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { since, until } = timeframe;

  useEffect(() => {
    let disposed = false;
    let controller: AbortController | null = null;
    let timer: number | undefined;

    function schedule() {
      timer = window.setTimeout(() => load(true), AUDIT_POLL_MS);
    }

    function handleResult(result: AuditEntry[]) {
      if (disposed) return;
      setEntries(result);
      setLoading(false);
      setError(null);
      schedule();
    }

    // A failed poll keeps the last good rows; only the visible load
    // surfaces the error. Aborts are the effect's own teardown, never a
    // failure to report.
    function handleFailure(err: unknown, background: boolean) {
      if (disposed) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (!background) {
        setError(
          err instanceof Error ? err.message : "Failed to load audit entries",
        );
        setLoading(false);
      }
      schedule();
    }

    // background=false is the visible load (mount / timeframe change):
    // skeletons up, errors surfaced. Polls stay silent — the table keeps
    // its rows through a failed refresh and just tries again next tick.
    function load(background: boolean) {
      controller = new AbortController();
      if (!background) {
        setLoading(true);
        setError(null);
      }
      auditApi
        .list(
          {
            limit: FETCH_LIMIT,
            since: since?.toISOString(),
            until: until?.toISOString(),
          },
          controller.signal,
        )
        .then(handleResult)
        .catch((err: unknown) => handleFailure(err, background));
    }

    load(false);
    return () => {
      disposed = true;
      controller?.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [since, until]);

  return { entries, loading, error };
}

/** "pipeline.delete" → "pipeline"; the group an entry's action files
 *  under in the sidebar tree. */
function actionGroupOf(action: string): string {
  const dot = action.indexOf(".");
  return dot === -1 ? action : action.slice(0, dot);
}

function formatActor(entry: AuditEntry): string {
  if (!entry.actor) return "anonymous";
  return (
    entry.actor.email ?? entry.actor.name ?? entry.actor.sub ?? "anonymous"
  );
}

// The header's text filter matches the visible columns.
function matchesFilterText(entry: AuditEntry, term: string): boolean {
  if (term === "") return true;
  return [
    entry.action,
    entry.resource,
    formatActor(entry),
    entry.node_name,
    entry.message ?? "",
  ].some((field) => field.toLowerCase().includes(term));
}

// Sidebar checkboxes + the text filter narrow the fetched window
// client-side. A fully-checked tree filters nothing, so entries with an
// action group or status class outside the known lists stay visible by
// default and only drop once the user narrows the tree.
interface EntryFilters {
  filterText: string;
  resourceFilter: string;
  actorFilter: string;
  selectedActions: ReadonlySet<AuditActionGroup>;
  selectedStatuses: ReadonlySet<number>;
}

const matchesResource = (entry: AuditEntry, resourceFilter: string) =>
  resourceFilter === "all" || entry.resource === resourceFilter;

// The agent pick matches on the actor's source; a named pick on the same
// display string the Actor column shows.
function matchesActor(entry: AuditEntry, actorFilter: string): boolean {
  if (actorFilter === "all") return true;
  if (actorFilter === AGENT_ACTOR_FILTER)
    return entry.actor?.source === "agent";
  return formatActor(entry) === actorFilter;
}

const matchesActionGroup = (
  entry: AuditEntry,
  selected: ReadonlySet<AuditActionGroup>,
) =>
  selected.size === AUDIT_ACTION_GROUPS.length ||
  selected.has(actionGroupOf(entry.action) as AuditActionGroup);

const matchesStatusClass = (entry: AuditEntry, selected: ReadonlySet<number>) =>
  selected.size === ALL_STATUS_CLASSES.length ||
  selected.has(Math.floor(entry.status_code / 100));

function useVisibleEntries(entries: AuditEntry[], filters: EntryFilters) {
  // Destructured so the memo keys off the stable pieces, not the filters
  // object the caller rebuilds every render.
  const {
    filterText,
    resourceFilter,
    actorFilter,
    selectedActions,
    selectedStatuses,
  } = filters;
  return useMemo(() => {
    const term = filterText.trim().toLowerCase();
    return entries.filter(
      (entry) =>
        matchesResource(entry, resourceFilter) &&
        matchesActor(entry, actorFilter) &&
        matchesActionGroup(entry, selectedActions) &&
        matchesStatusClass(entry, selectedStatuses) &&
        matchesFilterText(entry, term),
    );
  }, [
    entries,
    filterText,
    resourceFilter,
    actorFilter,
    selectedActions,
    selectedStatuses,
  ]);
}

function formatEventTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

const STATUS_BADGE_META = new Map(
  STATUS_CLASS_ROWS.map((row) => [row.statusClass, row]),
);

// The status cell's badge (Figma Table/tbody/column 15571-26609): the
// code with its class icon on the class color — 1xx/3xx info, 2xx
// confirm, 4xx/5xx error; codes outside the classes fall back to the
// neutral secondary face.
function StatusCodeBadge({ code }: { code: number }) {
  const theme = useTheme();
  const meta = STATUS_BADGE_META.get(Math.floor(code / 100));
  const type = meta?.type ?? BadgeType.Secondary;
  return (
    <GenericBadge type={type} mode={BadgeMode.Subtle}>
      {meta && (
        <IconSVG
          icon={meta.icon}
          size={12}
          colors={{ default: badgeIconColor(theme, type) }}
        />
      )}
      {code}
    </GenericBadge>
  );
}

// The "agent" badge marks entries the agent performed on the user's
// behalf (actor.source === "agent", TTTL-212), trailing the actor name
// (Figma Table/tbody/column/cell 15642-160074).
function AgentMark() {
  return (
    <Tooltip text="Performed by the AI agent" side="top">
      <AgentBadgeSlot>
        <AgentBadge />
      </AgentBadgeSlot>
    </Tooltip>
  );
}

// Entries download as a JSON file, matching what the API served.
function exportEntries(entries: AuditEntry[]) {
  const blob = new Blob([JSON.stringify(entries, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-export-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

const columnHelper = createColumnHelper<AuditEntry>();

// Skeleton rows mirror the real cell anatomy so columns line up with the
// data that replaces them (the Pipelines table's skeleton recipe).
const SKELETON_ROWS = Array.from({ length: 8 }, (_, i) => `skeleton-${i}`);

function SkeletonBodyRows() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <SkeletonRow key={key} aria-hidden="true">
          <TableCell>
            <CellText>
              <SkeletonBar $width="80%" />
            </CellText>
          </TableCell>
          <TableCell>
            <CellText>
              <SkeletonBar $width="65%" />
            </CellText>
          </TableCell>
          <TableCell>
            <BadgeSlot>
              <SkeletonPill $width="52px" />
            </BadgeSlot>
          </TableCell>
          <TableCell>
            <CellText>
              <SkeletonBar $width="70%" />
            </CellText>
          </TableCell>
          <TableCell>
            <CellText>
              <SkeletonBar $width="75%" />
            </CellText>
          </TableCell>
          <TableCell>
            <CellText>
              <SkeletonBar $width="70%" />
            </CellText>
          </TableCell>
          <TableCell>
            <CellText>
              <SkeletonBar $width="30%" />
            </CellText>
          </TableCell>
        </SkeletonRow>
      ))}
    </>
  );
}

// A plain tr alias — the skeleton rows carry aria-hidden, which the
// linter only tolerates on non-focusable custom components.
const SkeletonRow = styled.tr``;

// Mirrors the column's sort state onto the <th> for assistive tech;
// undefined leaves aria-sort off the non-sortable columns entirely.
function ariaSortFor(
  column: Column<AuditEntry, unknown>,
): "ascending" | "descending" | "none" | undefined {
  if (!column.getCanSort()) return undefined;
  const sorted = column.getIsSorted();
  if (sorted === "asc") return "ascending";
  if (sorted === "desc") return "descending";
  return "none";
}

function emptyMessage(error: string | null, hasAnyEntries: boolean): string {
  if (error) return `Failed to load audit entries — ${error}`;
  if (hasAnyEntries) return "No entries match the current filters";
  return "No audit entries yet — mutating API actions will appear here";
}

function AuditLogTable({
  entries,
  loading,
  error,
  hasAnyEntries,
  filterText,
  onFilterTextChange,
}: {
  entries: AuditEntry[];
  loading: boolean;
  error: string | null;
  /** Raw (unfiltered) entries exist — an empty visible list means the
   *  filters hid them, not that the log is empty. */
  hasAnyEntries: boolean;
  filterText: string;
  onFilterTextChange: (v: string) => void;
}) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: readStoredPageSize(),
  });
  // The design's Event time column sorts, newest first by default; the
  // other columns are plain (Figma marks only the first header).
  const [sorting, setSorting] = useState<SortingState>([
    { id: "timestamp", desc: true },
  ]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        header: "Event time",
        sortDescFirst: true,
        sortingFn: (rowA, rowB, columnId) => {
          const a = new Date(rowA.getValue<string>(columnId)).getTime();
          const b = new Date(rowB.getValue<string>(columnId)).getTime();
          return (Number.isNaN(a) ? 0 : a) - (Number.isNaN(b) ? 0 : b);
        },
        cell: (info) => <CellText>{formatEventTime(info.getValue())}</CellText>,
      }),
      columnHelper.accessor("action", {
        header: "Action",
        enableSorting: false,
        cell: (info) => <CellText>{info.getValue()}</CellText>,
      }),
      columnHelper.accessor("status_code", {
        header: "Status",
        enableSorting: false,
        cell: (info) => (
          <BadgeSlot>
            <StatusCodeBadge code={info.getValue()} />
          </BadgeSlot>
        ),
      }),
      columnHelper.accessor("resource", {
        header: "Resource",
        enableSorting: false,
        cell: (info) => (
          <CellText title={info.getValue()}>{info.getValue()}</CellText>
        ),
      }),
      columnHelper.accessor((entry) => formatActor(entry), {
        id: "actor",
        header: "Actor",
        enableSorting: false,
        // The title doubles as the full value for ellipsized emails. A
        // plain actor renders exactly like the Resource column's cell;
        // only agent rows get the name-plus-badge row.
        cell: (info) =>
          info.row.original.actor?.source === "agent" ? (
            <ActorCell>
              <ActorName title={info.getValue()}>{info.getValue()}</ActorName>
              <AgentMark />
            </ActorCell>
          ) : (
            <CellText title={info.getValue()}>{info.getValue()}</CellText>
          ),
      }),
      columnHelper.accessor("node_name", {
        header: "Node",
        enableSorting: false,
        cell: (info) => (
          <CellText title={info.row.original.worker_id}>
            {info.getValue()}
          </CellText>
        ),
      }),
      columnHelper.accessor((entry) => entry.message ?? "", {
        id: "message",
        header: "Message",
        enableSorting: false,
        cell: (info) => <CellText>{info.getValue() || "-"}</CellText>,
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: entries,
    columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: (updater) => {
      setSorting(updater);
      // Sorting reorders the whole set; jump back to the first page so
      // the new top rows are in view.
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // The sidebar filters swap the data array out from under the table;
    // the clamp effect below keeps the page in range instead of the
    // default yank back to page 0 on every change.
    autoResetPageIndex: false,
  });

  const total = entries.length;
  const pageCount = table.getPageCount();
  useEffect(() => {
    const maxIndex = Math.max(0, pageCount - 1);
    if (pagination.pageIndex > maxIndex) table.setPageIndex(maxIndex);
  }, [table, pageCount, pagination.pageIndex]);

  // What the export downloads: every entry that passes the filters, in
  // the current sort order — all pages, not just the visible one.
  const filteredSorted = table
    .getPrePaginationRowModel()
    .rows.map((row) => row.original);

  return (
    <TableRegion>
      {/* The card-wide filter input row (Figma Table/header 60px: 14px
          padding around the 32px underline input). */}
      <FilterRow>
        <GenericInput
          type={InputType.Text}
          mode={InputMode.Control}
          value={filterText}
          placeholder="Filter logs"
          onChange={(e) => onFilterTextChange(e.target.value)}
          allowClear={filterText.length > 0}
          onClear={() => onFilterTextChange("")}
        />
      </FilterRow>

      <TableScrollWrap>
        <StyledTableScroll>
          <Table>
            {/* 186/120 mirror the design's TableSimple column widths
                (13759-118888); the message column takes the rest. */}
            <colgroup>
              <col style={{ width: 186 }} />
              <col style={{ width: 186 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 186 }} />
              <col style={{ width: 186 }} />
              <col style={{ width: 186 }} />
              <col />
            </colgroup>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHeader
                      key={header.id}
                      aria-sort={ariaSortFor(header.column)}
                    >
                      {header.column.getCanSort() ? (
                        <SortableHeader
                          direction={header.column.getIsSorted() || null}
                          onClick={() => header.column.toggleSorting()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </SortableHeader>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHeader>
                  ))}
                </tr>
              ))}
            </TableHead>
            <tbody>
              {loading && <SkeletonBodyRows />}
              {!loading &&
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} data-testid="audit-row">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>

          {!loading && total === 0 && (
            <EmptyRowMessage data-testid="audit-empty">
              {emptyMessage(error, hasAnyEntries)}
            </EmptyRowMessage>
          )}
        </StyledTableScroll>
      </TableScrollWrap>

      {/* The footer bar (Figma Table/footer + AP_SystemLogs/actions):
          Export on the left, the paging controls hugging the right. */}
      <TablePagination
        pageIndex={pagination.pageIndex}
        pageCount={pageCount}
        pageSize={pagination.pageSize}
        total={total}
        pageSizes={PAGE_SIZES}
        onPageChange={(index) => table.setPageIndex(index)}
        onPageSizeChange={(size) => {
          table.setPageSize(size);
          table.setPageIndex(0);
          // Default stored as absence, like every other audit:* key.
          if (size === PAGE_SIZES[0]) {
            userStorage.removeItem(STORAGE_KEYS.pageSize);
          } else {
            userStorage.setItem(STORAGE_KEYS.pageSize, String(size));
          }
        }}
        leading={
          <ButtonMediumSecondaryLeadingIcon
            text="Export filtered results"
            icon={Icon.Export}
            disabled={loading || filteredSorted.length === 0}
            onClick={() => exportEntries(filteredSorted)}
          />
        }
      />
    </TableRegion>
  );
}

function AuditLog() {
  const [collapsed, setCollapsed] = usePersistedCollapsed();
  const [filterText, setFilterText] = usePersistedFilterText();
  const [resourceFilter, handleResourceChange] = usePersistedChoice(
    STORAGE_KEYS.resource,
  );
  const [actorFilter, handleActorChange] = usePersistedChoice(
    STORAGE_KEYS.actor,
  );
  const [timeframe, setTimeframe] = usePersistedState<Timeframe>(
    STORAGE_KEYS.timeframe,
    parseTimeframe,
    serializeTimeframe,
  );
  const {
    selected: selectedActions,
    toggle: toggleAction,
    setAll: setAllActions,
  } = usePersistedSelection<AuditActionGroup>(
    STORAGE_KEYS.actions,
    AUDIT_ACTION_GROUPS,
  );
  const {
    selected: selectedStatuses,
    toggle: toggleStatus,
    setAll: setAllStatuses,
  } = usePersistedSelection<number>(STORAGE_KEYS.statuses, ALL_STATUS_CLASSES);

  const { entries, loading, error } = useAuditEntries(timeframe);

  const allResources = useMemo(
    () => Array.from(new Set(entries.map((e) => e.resource))).sort(),
    [entries],
  );
  // Actors are keyed by the same display string the Actor column shows,
  // so a pick matches exactly what the user read off the table.
  const allActors = useMemo(
    () => Array.from(new Set(entries.map(formatActor))).sort(),
    [entries],
  );
  const hasAgentEntries = useMemo(
    () => entries.some((e) => e.actor?.source === "agent"),
    [entries],
  );

  const visibleEntries = useVisibleEntries(entries, {
    filterText,
    resourceFilter,
    actorFilter,
    selectedActions,
    selectedStatuses,
  });

  return (
    <div>
      {/* House list-page header (Figma Page/title 13759-112323): H2
          title with Export all on the title row — the raw fetched
          window, unfiltered; the footer's "Export filtered results"
          handles the narrowed set. */}
      <ListPageHeader
        title="Audit logs"
        action={
          <ButtonMediumSecondaryLeadingIcon
            text="Export all"
            icon={Icon.Export}
            disabled={entries.length === 0}
            onClick={() => exportEntries(entries)}
          />
        }
      />
      <Card>
        <AuditLogFilterSidebar
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          resourceFilter={resourceFilter}
          onResourceChange={handleResourceChange}
          allResources={allResources}
          actorFilter={actorFilter}
          onActorChange={handleActorChange}
          allActors={allActors}
          hasAgentEntries={hasAgentEntries}
          selectedActions={selectedActions}
          toggleAction={toggleAction}
          setAllActions={setAllActions}
          selectedStatuses={selectedStatuses}
          toggleStatus={toggleStatus}
          setAllStatuses={setAllStatuses}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
        <AuditLogTable
          entries={visibleEntries}
          loading={loading}
          error={error}
          hasAnyEntries={entries.length > 0}
          filterText={filterText}
          onFilterTextChange={setFilterText}
        />
      </Card>
    </div>
  );
}

/* The card splits into the filter sidebar and the table region (Figma
   TableCard 13759-112324). No overflow clipping — the sidebar's collapse
   tab overlaps the card's bottom border, so the corner rounding sits on
   the inner surfaces instead (the /logs card recipe). */
const Card = styled.div`
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 8px;
  display: flex;
  /* Fill the viewport: 122px = top bar (50) + page padding (10) + the
     sticky page header (52); the page's 10px bottom padding closes the
     gap under the card. */
  height: calc(100vh - 122px);
`;

/* Filter row, table and footer stack; the table hugs its rows (short
   pages leave card background under the footer) and scrolls internally
   once they outgrow the region. On narrow screens the REGION scrolls
   horizontally as one unit — filter row, table and footer keep the
   shared minimum width, so the footer's controls never squash or wrap
   and every border stays aligned. */
const TableRegion = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-x: auto;

  & > * {
    min-width: ${TABLE_MIN_WIDTH}px;
  }

  /* StyledTableScroll's frame stretches to 100% height by default; here
     it should hug the rows instead (the Pipelines TableCard override).
     Unlike Pipelines, the frame's TOP edge is kept — the design rules
     the table block above the header (Figma Table wrap, 13759-118888).
     The other three edges go: the sidebar divider and the card border
     already run at the sides, and the pagination bar's own border-top is
     the line under the block — each would double up. */
  .${styledTableClassNames.scroll} {
    border-left: none;
    border-right: none;
    border-bottom: none;
    height: auto;
    min-height: 0;
  }

  /* Keep the vertical scrollbar below the sticky header row. !important
     beats overlayscrollbars' own positioning rules. */
  .os-scrollbar-vertical {
    top: ${TABLE.thead.cell.height + 1}px !important;
  }
`;

/* Figma Table/header (60px, 15642-160074): table/padding (14) on every
   side of the 32px filter input, so its underline stops short of the
   sidebar divider and the card's right edge; the text sits on the
   input's own stock paddingw. */
const FilterRow = styled.div`
  flex-shrink: 0;
  padding: ${TABLE.padding}px;
`;

const TableScrollWrap = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Table = styled.table`
  width: 100%;
  min-width: ${TABLE_MIN_WIDTH}px;
  /* separate, not collapse: collapsed borders detach from position:
     sticky headers and scroll away with the body */
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  background: ${(p) => p.theme.tableTheadCellBackground};

  /* The frame's bottom edge runs right under the last row — its cells
     drop their own border so the two don't stack into a double line. */
  tbody tr:last-child td {
    border-bottom: none;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }
`;

const TableHead = styled.thead`
  background: ${(p) => p.theme.tableTheadCellBackground};

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${(p) => p.theme.tableTheadCellBackground};
  }
`;

const TableHeader = styled.th`
  text-align: left;
  height: ${TABLE.thead.cell.height}px;
  padding: 0 ${TABLE.thead.cell.paddingr}px 0 ${TABLE.thead.cell.paddingl}px;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.small.regular.fontWeight};
  color: ${(p) => p.theme.semanticTextLight};
  white-space: nowrap;
  border-bottom: 1px solid ${(p) => p.theme.tableBackground};
  border-right: 1px solid ${(p) => p.theme.tableBackground};

  /* Column separators are the gaps *between* columns; the last column
     meets the card edge without its own line. */
  &:last-child {
    border-right: none;
  }
`;

const TableCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(p) => p.theme.semanticTextBody};
  vertical-align: middle;
  border-bottom: 1px solid ${(p) => p.theme.tableBackground};
  border-right: 1px solid ${(p) => p.theme.tableBackground};
  overflow: hidden;
`;

// Figma nests cell content in a wrapper with table/tbody/cell/gap (6px)
// horizontal padding on top of the 4px cell padding.
const CellText = styled.span`
  display: block;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  color: ${(p) => p.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The tooltip's hover target around the badge; the badge component is a
// block-level flex container by default, so it hugs its content here.
const AgentBadgeSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

// An agent row's actor cell (Figma Table/column/fill/item): the name fills
// the row and ellipsizes, the badge trails it at its own width. The item
// carries the table's 6px gap as its inset and between its children, on
// top of the cell's 4px padding. Plain rows skip this wrapper and render
// CellText, like the Resource column.
const ActorCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${TABLE.tbody.cell.gap}px;
  min-width: 0;
  padding: 0 ${TABLE.tbody.cell.gap}px;
`;

// The name inside ActorCell: CellText's ellipsis without its inset, which
// the wrapper already provides.
const ActorName = styled(CellText)`
  flex: 1 1 0;
  min-width: 0;
  padding: 0;
`;

// Badges hug their content rather than stretching to the cell width (the
// badge component renders as a block-level flex container by default).
const BadgeSlot = styled.div`
  padding-left: ${TABLE.tbody.cell.gap}px;

  & > * {
    display: inline-flex;
  }
`;

/* The tables' in-table empty message (the Pipelines EmptyRowMessage). */
const EmptyRowMessage = styled.div`
  /* Figma: semantic/spacingxl (40px) — token not yet in @studio3t/design-variables */
  padding: 40px 0;
  text-align: center;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  color: ${(p) => p.theme.semanticTextLight};
`;

export default AuditLog;
